import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import { Breadcrumbs, BreadcrumbItem, EmptyState, LoadingState } from '../components/ui';
import { alert } from '../utils/toast';
import type { Product, ProductEndpoint, KongConfiguration } from '../types';
import { ProductWizard } from '../components/products/ProductWizard';
import { EndpointManagement } from '../components/products/EndpointManagement';
import { TransformationBuilder } from '../components/products/TransformationBuilder';
import { OpenAPIUploader } from '../components/products/OpenAPIUploader';
import { KongSyncDashboard } from '../components/products/KongSyncDashboard';
import { EndpointTester } from '../components/products/EndpointTester';
import { ProductPlans } from '../components/products/ProductPlans';
import {
  ArrowLeft,
  FileText,
  Link2,
  Shuffle,
  Code,
  Cpu,
  CreditCard,
  FlaskConical,
  Globe,
  EyeOff,
  Package,
} from 'lucide-react';

type TabType =
  | 'overview'
  | 'endpoints'
  | 'transformations'
  | 'openapi'
  | 'kong'
  | 'testing'
  | 'planos';

export const ProductConfiguration: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [selectedEndpoint, setSelectedEndpoint] = useState<ProductEndpoint | null>(null);

  const { data: product, isLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      const response = await api.get<{ data: Product }>(`/portal/admin/products/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });

  const { data: endpoints } = useQuery({
    queryKey: ['product-endpoints', id],
    queryFn: async () => {
      const response = await api.get<{ data: ProductEndpoint[] }>(
        `/portal/admin/products/${id}/endpoints`
      );
      return response.data.data;
    },
    enabled: !!id,
  });

  const { data: kongStatus } = useQuery({
    queryKey: ['kong-status', id],
    queryFn: async () => {
      const response = await api.get<{ data: { configurations: KongConfiguration[] } }>(
        `/portal/admin/kong/sync-status/${id}`
      );
      return response.data.data.configurations;
    },
    enabled: !!id && activeTab === 'kong',
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/portal/admin/products/${id}/publish`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', id] });
      void alert.success('Publicado!', 'Produto publicado com sucesso!');
    },
    onError: (error) => {
      void alert.error(
        'Erro de Publicação',
        `Não foi possível publicar: ${getErrorMessage(error)}`
      );
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/portal/admin/products/${id}/unpublish`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', id] });
      void alert.success('Despublicado!', 'Produto despublicado com sucesso!');
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Breadcrumbs>
          <BreadcrumbItem href="/">Início</BreadcrumbItem>
          <BreadcrumbItem href="/products">Produtos</BreadcrumbItem>
          <BreadcrumbItem>A carregar...</BreadcrumbItem>
        </Breadcrumbs>
        <LoadingState type="card" count={2} />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Breadcrumbs>
          <BreadcrumbItem href="/">Início</BreadcrumbItem>
          <BreadcrumbItem href="/products">Produtos</BreadcrumbItem>
          <BreadcrumbItem>Não encontrado</BreadcrumbItem>
        </Breadcrumbs>
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-12 text-center border border-slate-200 dark:border-slate-800 shadow-xs">
          <EmptyState
            title="Produto não encontrado"
            description="O produto que está a tentar configurar não existe ou foi removido."
          />
          <Link
            to="/products"
            className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mt-4 font-semibold text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Voltar para os Produtos</span>
          </Link>
        </div>
      </div>
    );
  }

  const tabs: { key: TabType; label: string; icon: React.ReactNode; badge?: number }[] = [
    { key: 'overview', label: 'Visão Geral', icon: <FileText className="w-4 h-4" /> },
    {
      key: 'endpoints',
      label: 'Endpoints',
      icon: <Link2 className="w-4 h-4" />,
      badge: endpoints?.length || 0,
    },
    { key: 'transformations', label: 'Transformações', icon: <Shuffle className="w-4 h-4" /> },
    { key: 'openapi', label: 'OpenAPI Spec', icon: <Code className="w-4 h-4" /> },
    { key: 'kong', label: 'Sincronização Kong', icon: <Cpu className="w-4 h-4" /> },
    { key: 'planos', label: 'Planos de Preço', icon: <CreditCard className="w-4 h-4" /> },
    { key: 'testing', label: 'Testes de API', icon: <FlaskConical className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem href="/products">Produtos</BreadcrumbItem>
        <BreadcrumbItem>{product.name}</BreadcrumbItem>
      </Breadcrumbs>

      {/* Executive Product Header Card */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-xs space-y-5">
        <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
          <div className="space-y-2">
            <Link
              to="/products"
              className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-xs font-semibold uppercase tracking-wider transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Voltar ao Catálogo de Produtos</span>
            </Link>

            <div className="flex flex-wrap items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-950/60 border border-primary-200 dark:border-primary-800 flex items-center justify-center text-primary-600 dark:text-primary-400 shrink-0">
                <Package className="w-5 h-5" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                {product.name}
              </h1>
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${
                  product.published
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/50'
                    : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    product.published ? 'bg-emerald-500' : 'bg-slate-400'
                  }`}
                />
                {product.published ? 'Publicado no Portal Dev' : 'Rascunho Interno'}
              </span>
            </div>

            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base max-w-3xl">
              {product.description}
            </p>

            {/* Metadata Pills */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                Versão: <strong>v{product.version || 1}</strong>
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                Autenticação: <strong>{product.authentication_provider || 'BASIC_AUTH'}</strong>
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                Endpoints: <strong>{endpoints?.length || 0} configurados</strong>
              </span>
              {product.external_url_sandbox && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/50">
                  Sandbox:{' '}
                  <code className="font-mono text-[11px]">{product.external_url_sandbox}</code>
                </span>
              )}
              {product.external_url_production && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-800/50">
                  Produção:{' '}
                  <code className="font-mono text-[11px]">{product.external_url_production}</code>
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2.5 shrink-0">
            {product.published ? (
              <button
                onClick={() => unpublishMutation.mutate()}
                disabled={unpublishMutation.isPending}
                className="btn-base btn-md btn-secondary flex items-center gap-2"
              >
                <EyeOff className="w-4 h-4 text-slate-500" />
                <span>{unpublishMutation.isPending ? 'A despublicar...' : 'Despublicar'}</span>
              </button>
            ) : (
              <button
                onClick={() => publishMutation.mutate()}
                disabled={publishMutation.isPending}
                className="btn-base btn-md bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2 shadow-xs"
              >
                <Globe className="w-4 h-4" />
                <span>{publishMutation.isPending ? 'A publicar...' : 'Publicar Produto'}</span>
              </button>
            )}
          </div>
        </div>

        {/* Clean Navigation Tabs inside Card */}
        <div className="border-t border-slate-200 dark:border-slate-800 pt-2 -mb-2">
          <nav className="flex space-x-2 overflow-x-auto pb-1" aria-label="Tabs">
            {tabs.map((tab) => {
              const isSelected = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
                    px-3.5 py-2 rounded-xl font-semibold text-sm whitespace-nowrap flex items-center gap-2 transition-all
                    ${
                      isSelected
                        ? 'bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-400 shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800/60'
                    }
                  `}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                  {typeof tab.badge === 'number' && (
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[11px] font-bold ${
                        isSelected
                          ? 'bg-primary-600 text-white'
                          : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'overview' && (
          <ProductWizard
            product={product}
            onUpdate={() => queryClient.invalidateQueries({ queryKey: ['product', id] })}
          />
        )}

        {activeTab === 'endpoints' && (
          <EndpointManagement
            productId={product.id}
            endpoints={endpoints || []}
            onEndpointSelect={setSelectedEndpoint}
            onUpdate={() => queryClient.invalidateQueries({ queryKey: ['product-endpoints', id] })}
          />
        )}

        {activeTab === 'transformations' && (
          <TransformationBuilder
            productId={product.id}
            endpoints={endpoints || []}
            selectedEndpoint={selectedEndpoint}
            onEndpointSelect={setSelectedEndpoint}
          />
        )}

        {activeTab === 'openapi' && (
          <OpenAPIUploader
            productId={product.id}
            currentSpec={product.openapi_spec}
            onUpdate={() => queryClient.invalidateQueries({ queryKey: ['product', id] })}
            endpoints={endpoints || []}
          />
        )}

        {activeTab === 'kong' && (
          <KongSyncDashboard
            productId={product.id}
            kongConfigurations={kongStatus || []}
            onSync={() => queryClient.invalidateQueries({ queryKey: ['kong-status', id] })}
          />
        )}

        {activeTab === 'testing' && (
          <EndpointTester
            productId={product.id}
            endpoints={endpoints || []}
            selectedEndpoint={selectedEndpoint}
            onEndpointSelect={setSelectedEndpoint}
          />
        )}

        {activeTab === 'planos' && <ProductPlans productId={product.id} />}
      </div>
    </div>
  );
};
export default ProductConfiguration;
